'use client'

import * as React from 'react'
import { Checkbox as RadixCheckbox } from 'radix-ui'
import { faCheckSquare } from '@fortawesome/pro-solid-svg-icons'
import { mtc } from '../../../utils/style-helpers'
import { Icon } from '../icon'
import { checkboxIndicatorStyles, checkboxStyles, labelStyles } from './styles'

type NativeProps = Omit<
  React.ComponentPropsWithoutRef<typeof RadixCheckbox.Root>,
  'checked' | 'defaultChecked' | 'onCheckedChange' | 'onChange'
>

export interface CheckboxProps extends NativeProps {
  checked?: boolean // controlled
  className?: string
  defaultChecked?: boolean // uncontrolled
  disabled?: boolean
  id?: string
  label?: string | React.ReactNode
  onCheckedChange?: (checked: boolean) => void
  ariaLabel?: string // for accessibility
}

export const Checkbox = React.forwardRef<
  React.ElementRef<typeof RadixCheckbox.Root>,
  CheckboxProps
>(
  (
    {
      checked,
      className,
      disabled = false,
      defaultChecked,
      id,
      label,
      onCheckedChange,
      ariaLabel,
      ...props
    },
    ref
  ) => {
    const isControlled = checked !== undefined
    const labelString = typeof label === 'string' ? label : ariaLabel
    const checkboxId =
      id || `checkbox-${labelString?.toLowerCase().replace(/\s+/g, '-')}`

    return (
      <div className="flex items-center gap-2">
        <RadixCheckbox.Root
          ref={ref}
          id={checkboxId}
          disabled={disabled}
          checked={isControlled ? checked : undefined}
          defaultChecked={!isControlled ? defaultChecked : undefined}
          onCheckedChange={(val: boolean) => {
            onCheckedChange?.(val)
          }}
          className={mtc(checkboxStyles(), className)}
          aria-label={labelString}
          {...props}
        >
          <RadixCheckbox.Indicator className={mtc(checkboxIndicatorStyles())}>
            <Icon icon={faCheckSquare} variant="checkbox" />
          </RadixCheckbox.Indicator>
        </RadixCheckbox.Root>

        {label && (
          <label
            htmlFor={checkboxId}
            className={mtc(labelStyles({ disabled }))}
          >
            {label}
          </label>
        )}
      </div>
    )
  }
)

Checkbox.displayName = 'Checkbox'
